import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Eye, EyeOff, Wand2, Copy } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Input } from "../ui/input";
import { Checkbox } from "../ui/checkbox";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../ui/select";
import { PermissionMatrix } from "./PermissionMatrix";
import { apiClient, getApiErrorMessage } from "../../lib/apiClient";

const UPPER = "ABCDEFGHJKLMNPQRSTUVWXYZ";
const LOWER = "abcdefghijkmnpqrstuvwxyz";
const DIGIT = "23456789";
const SPECIAL = "!@#$%^&*";
function strongPassword(len = 14) {
  const all = UPPER + LOWER + DIGIT + SPECIAL;
  let pw = UPPER[Math.floor(Math.random() * UPPER.length)]
    + LOWER[Math.floor(Math.random() * LOWER.length)]
    + DIGIT[Math.floor(Math.random() * DIGIT.length)]
    + SPECIAL[Math.floor(Math.random() * SPECIAL.length)];
  for (let i = pw.length; i < len; i++) pw += all[Math.floor(Math.random() * all.length)];
  return pw.split("").sort(() => Math.random() - 0.5).join("");
}

const EMPTY = {
  full_name: "", username: "", email: "", password: "",
  role_template: "", permissions: [], is_super_admin: false, active: true,
};

export function AdminFormDialog({ open, onOpenChange, onSaved, admin, catalog }) {
  const isEdit = Boolean(admin);
  const [form, setForm] = useState(EMPTY);
  const [showPw, setShowPw] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setShowPw(false);
    setForm(admin ? {
      full_name: admin.full_name || "", username: admin.username || "",
      email: admin.email || "", password: "",
      role_template: admin.role_template || "", permissions: admin.permissions || [],
      is_super_admin: !!admin.is_super_admin, active: !!admin.active,
    } : EMPTY);
  }, [open, admin]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const applyTemplate = (tplKey) => {
    const tpl = catalog?.role_templates?.[tplKey];
    setForm((f) => ({
      ...f,
      role_template: tplKey,
      permissions: tpl ? [...tpl.permissions] : f.permissions,
      is_super_admin: tplKey === "super_admin" ? true : f.is_super_admin,
    }));
  };

  const copyPw = async () => {
    if (!form.password) return;
    try { await navigator.clipboard.writeText(form.password); toast.success("Password copied"); }
    catch { toast.error("Couldn't copy"); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.username.trim()) { toast.error("Username is required."); return; }
    setSaving(true);
    try {
      if (isEdit) {
        await apiClient.put(`/admins/${admin.id}`, {
          full_name: form.full_name, username: form.username, email: form.email,
          role_template: form.role_template || null, permissions: form.permissions,
          is_super_admin: form.is_super_admin, active: form.active,
        });
        toast.success("Admin updated");
        onSaved(null);
      } else {
        const { data } = await apiClient.post("/admins", {
          full_name: form.full_name, username: form.username,
          email: form.email || null, password: form.password || null,
          role_template: form.role_template || null, permissions: form.permissions,
          is_super_admin: form.is_super_admin, active: form.active,
        });
        toast.success("Admin created");
        onSaved(data); // carries new_password for the one-time credential dialog
      }
      onOpenChange(false);
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Could not save admin."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid="admin-form-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit Admin" : "Create Admin"}</DialogTitle>
          <DialogDescription>
            {isEdit ? "Update this admin's details, role and permissions."
              : "Create an admin account, choose a role template, then fine-tune permissions."}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="af-fullname">Full Name</Label>
              <Input id="af-fullname" data-testid="admin-form-fullname" value={form.full_name} onChange={(e) => set("full_name", e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="af-username">Username / Login ID</Label>
              <Input id="af-username" data-testid="admin-form-username" value={form.username} onChange={(e) => set("username", e.target.value)} required />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="af-email">Email</Label>
              <Input id="af-email" type="email" data-testid="admin-form-email" value={form.email} onChange={(e) => set("email", e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>Role Template</Label>
              <Select value={form.role_template} onValueChange={applyTemplate}>
                <SelectTrigger data-testid="admin-form-role-select"><SelectValue placeholder="Choose a template…" /></SelectTrigger>
                <SelectContent>
                  {Object.entries(catalog?.role_templates || {}).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {!isEdit && (
            <div className="space-y-1.5">
              <Label htmlFor="af-password">Password <span className="text-text-muted">(leave blank to auto-generate)</span></Label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Input
                    id="af-password" type={showPw ? "text" : "password"} className="pr-10"
                    data-testid="admin-form-password" value={form.password}
                    onChange={(e) => set("password", e.target.value)} autoComplete="new-password"
                  />
                  <button type="button" onClick={() => setShowPw((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted hover:text-text-main"
                    data-testid="admin-form-password-toggle" aria-label="Toggle password">
                    {showPw ? <EyeOff size={16} strokeWidth={1.5} /> : <Eye size={16} strokeWidth={1.5} />}
                  </button>
                </div>
                <Button type="button" variant="outline" onClick={() => { set("password", strongPassword()); setShowPw(true); }} data-testid="admin-form-generate-password">
                  <Wand2 size={15} strokeWidth={1.5} /> Generate
                </Button>
                <Button type="button" variant="outline" onClick={copyPw} disabled={!form.password} data-testid="admin-form-copy-password">
                  <Copy size={15} strokeWidth={1.5} />
                </Button>
              </div>
            </div>
          )}

          <div className="flex flex-wrap items-center gap-6">
            <label className="flex items-center gap-2 text-sm">
              <Checkbox checked={form.active} onCheckedChange={(v) => set("active", !!v)} data-testid="admin-form-active" />
              <span>Active</span>
            </label>
            <label className="flex items-center gap-2 text-sm">
              <Checkbox checked={form.is_super_admin} onCheckedChange={(v) => set("is_super_admin", !!v)} data-testid="admin-form-super" />
              <span>Super Admin <span className="text-text-muted">(full access)</span></span>
            </label>
          </div>

          <div className="space-y-2">
            <Label>Permissions</Label>
            {form.is_super_admin && (
              <p className="text-xs text-info" data-testid="admin-form-super-note">
                Super Admin has every permission — the matrix below is locked.
              </p>
            )}
            <PermissionMatrix
              catalog={catalog}
              value={form.permissions}
              onChange={(p) => set("permissions", p)}
              disabled={form.is_super_admin}
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="secondary" onClick={() => onOpenChange(false)} data-testid="admin-form-cancel">Cancel</Button>
            <Button type="submit" variant="accent" disabled={saving} data-testid="admin-form-submit">
              {saving ? "Saving…" : isEdit ? "Save Changes" : "Create Admin"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
